import React, { useState } from 'react';
import { Typography } from '@mui/material';
import { styled, keyframes } from '@mui/system';
import { COLOR_PRIMARY_2 } from 'src/colors';
import { exchangeRates } from 'src/const';
import { convertCurrency, formatCurrency } from 'src/utils/functions';

const buzzAnimation = keyframes`
  10%, 90% { transform: translateX(-1px); }
  20%, 80% { transform: translateX(2px); }
  30%, 70% { transform: translateX(-2px); }
  40%, 60% { transform: translateX(1px); }
  50% { transform: translateX(-1px); }
`;

const Bubble = styled('div')({
  position: 'relative',
  backgroundColor: COLOR_PRIMARY_2,
  color: '#fff',
  borderRadius: '12px',
  padding: '4px 8px',
  textAlign: 'center',
  width: '60px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
  '&.hovered': {
    animation: `${buzzAnimation} 0.5s ease`,
  },
});

const Pointer = styled('div')({
  position: 'absolute',
  bottom: -6,
  left: '50%',
  transform: 'translateX(-50%)',
  width: 0,
  height: 0,
  borderLeft: '6px solid transparent',
  borderRight: '6px solid transparent',
  borderTop: `6px solid ${COLOR_PRIMARY_2}`,
});

const SmallBadge = styled('div')({
  position: 'absolute',
  top: -14,
  right: -10,
  backgroundColor: '#fff',
  color: COLOR_PRIMARY_2,
  borderRadius: '9999px',
  padding: '2px 6px',
  fontSize: '0.75rem',
  fontWeight: 600,
  border: `1px solid ${COLOR_PRIMARY_2}`,
});

const ClusterPointer = ({
  id,
  lat,
  lng,
  supply,
  performanceMetrics,
  clusterKey,
  currency,
  shouldFade,
  color,
  handleOnClick,
}: {
  id: number;
  lat: number;
  lng: number;
  supply: number;
  performanceMetrics: {
    adr: number;
    occupancyRate: number;
    revPar: number;
  };
  clusterKey: string;
  currency: string;
  shouldFade: boolean;
  color: string;
  handleOnClick: () => void;
}) => {
  const metric = performanceMetrics[clusterKey];
  const currencySymbol = exchangeRates[currency].symbol;

  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleOnClick}
    >
      <Bubble
        className={isHovered ? 'hovered' : ''}
        style={{
          backgroundColor: color,
          width: shouldFade && !isHovered ? '15px' : '60px',
        }}
      >
        <Typography
          style={{
            fontSize: '0.65rem',
            lineHeight: shouldFade && !isHovered ? 0.25 : 1,
            visibility: shouldFade && !isHovered ? 'hidden' : 'visible',
          }}
        >
          {supply} listings
        </Typography>
        {metric ? (
          <Typography
            style={{
              fontSize: '1rem',
              fontWeight: 700,
              lineHeight: shouldFade && !isHovered ? 0.3 : 1.2,
              visibility: shouldFade && !isHovered ? 'hidden' : 'visible',
            }}
          >
            {clusterKey === 'occupancyRate'
              ? `${metric}%`
              : `${currencySymbol}${formatCurrency(String(convertCurrency(currency, metric)))}`}
          </Typography>
        ) : (
          <Typography
            style={{
              fontSize: '1rem',
              fontWeight: 700,
              lineHeight: shouldFade && !isHovered ? 0.3 : 1.2,
              visibility: shouldFade && !isHovered ? 'hidden' : 'visible',
            }}
          >
            N/A
          </Typography>
        )}
        <Pointer
          style={{
            borderTop: `6px solid ${color}`,
          }}
        />
      </Bubble>
    </div>
  );
};

export default ClusterPointer;
